import {syncGetTrackLines} from '@gaia/utils/gaia/getTrackLines';
import type {GetSyncPromise} from '@gaia/utils/react/useGetSyncPromise';
import {filterNonNullable} from '@gaia/utils/typing';
import {useHighlightLocation} from '@map/Map/store/highlightLocation';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import type {Feature, MultiLineString} from 'geojson';
import {LngLat, LngLatLike} from 'mapbox-gl';
import type {CSSProperties} from 'react';

import lineLocationStyles from './LineLocation.module.css';

interface LineLocationProps {
    project: NonNullable<MapProject>;
    location: LngLatLike;
    classes?: string;
    style?: CSSProperties;
}

interface HighlightLocationProps {
    project: MapProject;
}

interface HoverLineLocationProps {
    project: MapProject;
    interaction: InteractionState;
    getSyncPromise: GetSyncPromise;
}

// augment CSSProperties type w/ custom properties used in this module
declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    interface CSSProperties {
        '--x'?: string;
        '--y'?: string;
        '--border-color'?: string;
    }
}

function LineLocation({
    project,
    location,
    classes = lineLocationStyles.highlightLocation,
    style = {},
}: LineLocationProps) {
    const projection = project(location);

    // Might be more performant to memoize the `style` prop and set `--x` and `--y` directly on the
    // <div> inside a `useLayoutEffect` hook, but for now this is simpler and easier to understand.
    //
    // I.e.,
    //
    //     useLayoutEffect(() => {
    //         ref.current!.style.setProperty('--x', `${projection.x}px`);
    //         ref.current!.style.setProperty('--y', `${projection.y}px`);
    //     }, [projection]);
    //
    // But realistically, the computation involved in `project` is likely more expensive than the
    // DOM updates, not to mention the expensive math done by HoverLineLocation.

    style = {
        ...style,
        '--x': `${projection.x}px`,
        '--y': `${projection.y}px`,
    };

    return <div className={classes} style={style} />;
}

const hiddenHoverLocation = <div style={{display: 'none'}} />;

export function HoverLineLocation({project, interaction, getSyncPromise}: HoverLineLocationProps) {
    if (interaction.type !== 'editingRoute') return hiddenHoverLocation;
    if (interaction.subtype !== 'hoveringLine') return hiddenHoverLocation;
    if (project === undefined) return hiddenHoverLocation;

    const [trackLines, error] = syncGetTrackLines({
        track: interaction.track,
        project,
        interaction,
        getSyncPromise,
        includeAppendPreviewLine: false,
    });

    if (error) {
        console.error(error);
        return null;
    }

    const coordinates = trackLines.filter(filterNonNullable).map((f) => f.geometry.coordinates);

    const lineAsGeoJson: Feature<MultiLineString> = {
        type: 'Feature',
        properties: {},
        geometry: {type: 'MultiLineString', coordinates},
    };

    const pointOnLine = nearestPointOnLine(lineAsGeoJson, interaction.lngLat.toArray());
    const lngLat = pointOnLine.geometry.coordinates.slice(0, 2) as [number, number];

    try {
        const location = LngLat.convert(lngLat);

        return (
            <LineLocation
                project={project}
                location={location}
                classes={lineLocationStyles.hoverLineLocation}
                style={{'--border-color': interaction.track.properties.color}}
            />
        );
    } catch {
        console.error('LngLat conversion failed', lngLat, pointOnLine);
        return null;
    }
}

const hiddenHighlightLocation = <div style={{display: 'none'}} />;

export function HighlightLocation({project}: HighlightLocationProps) {
    const location = useHighlightLocation();

    if (project === undefined) return hiddenHighlightLocation;
    if (location === undefined) return hiddenHighlightLocation;

    return (
        <LineLocation
            project={project}
            location={location}
            classes={lineLocationStyles.highlightLocation}
        />
    );
}
